import { animate, style, transition, trigger } from '@angular/animations';
import { Component, DestroyRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
  API,
  ChannelDTO,
  DeviceAttributeDTO,
  DeviceFullDTO,
  LocationDTO,
  MediumTransformationDTO,
  Pagination,
  PlayerHardwareDTO,
  PlayerSoftwareDTO,
  PoolDTO,
  ScreenDTO,
  ScreenHardwareDTO,
} from 'src/app/services/api/api';
import { ModalNavigationComponent } from 'src/app/services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from 'src/app/services/gui/modal/modal-navigation.service';
import { ErrorService } from '../../../../services/error.service';
import { SbDevicePairingComponent } from '../sb-device-pairing/sb-device-pairing.component';
import { SbDevicePlaylistDownloadComponent } from '../sb-device-playlist-download/sb-device-playlist-download.component';
import { SbDeviceServiceComponent } from '../sb-device-service/sb-device-service.component';
import { SbDeviceDeletionComponent } from '../sb-device-deletion/sb-device-deletion.component';
import { UtilService } from 'src/app/services/util.service';
import { SearchSelectOptions } from 'src/app/services/gui/modal/modal.model';
import { SbSearchSelectModalComponent } from 'src/app/services/gui/modal/sb-search-select-modal/sb-search-select-modal.component';
import { BehaviorSubject, Subscription, switchMap } from 'rxjs';
import { MsOptions } from '../../../../services/global/global-interfaces';
import { SbDeviceExitPlayerComponent } from '../sb-device-exit-player/sb-device-exit-player.component';
import { SbDeviceRebootComponent } from '../sb-device-reboot/sb-device-reboot.component';
import { PermissionService } from '../../../../services/permission.service';
import { SbDeviceLocalDataComponent } from '../sb-device-local-data/sb-device-local-data.component';
import { SbDeviceLocalSettingComponent } from '../sb-device-local-setting/sb-device-local-setting.component';
import { SbDeviceCalibrationComponent } from '../sb-device-calibration/sb-device-calibration.component';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SbDevicePlaylistCache } from '../sb-device-playlist-cache/sb-device-playlist-cache.component';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';

@Component({
  selector: 'sb-comp-device-options',
  templateUrl: './sb-device-options.component.html',
  styleUrls: ['./sb-device-options.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate(250, style({ opacity: 1 }))]),
      transition(':leave', [animate(500, style({ opacity: 0 }))]),
    ]),
  ],
  standalone: false,
})
export class SbDeviceOptionsComponent implements OnInit, ModalNavigationComponent, OnDestroy {
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);
  isInModalContext: boolean;

  loading: boolean = false;

  @Input() device: DeviceFullDTO;

  pools: PoolDTO[] = [];

  channels: ChannelDTO[] = [];

  playerSoftwareList: PlayerSoftwareDTO[] = [];
  playerHardwareList: PlayerHardwareDTO[] = [];
  screenHardwareList: ScreenHardwareDTO[] = [];
  mediumTransformations: MediumTransformationDTO[] = [];

  paginationPools: BehaviorSubject<Pagination>;
  paginationChannels: BehaviorSubject<Pagination>;

  identifier: string;
  name: string;

  msPlayerHardwareOptions: MsOptions;
  msPlayerSoftwareOptions: MsOptions;
  msScreenHardwareOptions: MsOptions;
  msMediumTransformationsOptions: MsOptions;
  isDeviceMobile: boolean;
  duration: number;
  private subs: Subscription[] = [];

  constructor(
    private readonly api: API,
    private readonly modalNavigation: ModalNavigationService,
    private readonly errorHandler: ErrorService,
    public readonly util: UtilService,
    public readonly permissionService: PermissionService
  ) {
    this.isDeviceMobile = false;
    this.duration = 60;
  }

  ngOnInit() {
    this.paginationPools = new BehaviorSubject<Pagination>({ page: 1, count: 99, pages: -1, resultcount: -1 });
    this.paginationChannels = new BehaviorSubject<Pagination>({ page: 1, count: 99, pages: -1, resultcount: -1 });
    const paginationMediumTransformation = new BehaviorSubject<Pagination>({ page: 1, count: 8, pages: -1, resultcount: -1 });

    if (this.permissionService.hasPermission(this.permissionService.Permission.pool_list)) {
      const sub = this.api
        .ss_nm_Pool_retrieveAll({}, this.paginationPools)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (e: PoolDTO[]) => {
            this.pools = e;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
      this.subs.push(sub);
    }

    if (this.permissionService.hasPermission(this.permissionService.Permission.channel_list)) {
      const sub = this.api
        .ss_nm_Channel_retrieveAll({}, this.paginationChannels)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (e: ChannelDTO[]) => {
            this.channels = e;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
      this.subs.push(sub);
    }
    if (this.permissionService.hasPermission(this.permissionService.Permission.medium_transformation_list)) {
      const sub = this.api
        .ss_nm_MediumTransformation_retrieveAll({}, paginationMediumTransformation)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (e: MediumTransformationDTO[]) => {
            this.mediumTransformations = e;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
      this.subs.push(sub);
    }

    if (this.isInModalContext) {
      this.device = this.modalNavigation.getCurrentNavigation().data.device;
    }
    if (this.modalNavigation.getSharedData().locations && this.modalNavigation.getSharedData().locations.length >= 1) {
      this.device.location = this.modalNavigation.getSharedData().locations[0];
    }
    if (this.modalNavigation.getSharedData().noLocationSelected) {
      this.device.location = null;
    }
    this.identifier = this.device ? this.device.identifier : null;
    if (this.modalNavigation.getSharedData().identifier) {
      this.identifier = this.modalNavigation.getSharedData().identifier;
    }
    this.name = this.device ? this.device.name : null;
    if (this.modalNavigation.getSharedData().name) {
      this.name = this.modalNavigation.getSharedData().name;
    }
    if (this.device?.positionValidityDuration) {
      this.duration = this.device.positionValidityDuration;
    }
    this.isDeviceMobile = !!this.device?.positionValidityDuration;
  }

  downloadOfflinePlaylist() {
    if (this.isInModalContext) {
      this.modalNavigation.navigate(SbDevicePlaylistDownloadComponent, { device: this.device });
    }
  }

  service() {
    if (this.isInModalContext) {
      this.modalNavigation.navigate(SbDeviceServiceComponent, { device: this.device });
    }
  }

  pair() {
    if (this.isInModalContext) {
      this.modalNavigation.navigate(SbDevicePairingComponent, { device: this.device, mode: 'PAIR' });
    }
  }

  unpair() {
    if (this.isInModalContext) {
      this.modalNavigation.navigate(SbDevicePairingComponent, { device: this.device, mode: 'UNPAIR' });
    }
  }

  deleteDevice() {
    if (this.isInModalContext) {
      this.modalNavigation.navigate(SbDeviceDeletionComponent, { device: this.device });
    }
  }

  exitPlayer() {
    if (this.isInModalContext) {
      this.modalNavigation.navigate(SbDeviceExitPlayerComponent, { device: this.device });
    }
  }

  rebootDevice() {
    if (this.isInModalContext) {
      this.modalNavigation.navigate(SbDeviceRebootComponent, { device: this.device });
    }
  }

  clearLocalData() {
    if (this.isInModalContext) {
      this.modalNavigation.navigate(SbDeviceLocalDataComponent, { device: this.device });
    }
  }

  calibrationImage() {
    if (this.isInModalContext) {
      this.modalNavigation.navigate(SbDeviceCalibrationComponent, { device: this.device });
    }
  }

  clearLocalSetting() {
    if (this.isInModalContext) {
      this.modalNavigation.navigate(SbDeviceLocalSettingComponent, { device: this.device });
    }
  }

  ok() {
    this.loading = true;
    this.device.identifier = this.identifier?.length > 0 ? this.identifier : null;
    this.device.name = this.name?.length > 0 ? this.name : null;
    if (this.isDeviceMobile) {
      this.device.positionValidityDuration = this.duration;
    } else {
      this.device.positionValidityDuration = null;
    }

    this.api
      .ca_md_DeviceAttribute_editAttributes(this.device.attributes)
      .pipe(
        switchMap(() => this.api.ss_nm_Device_editDevice(this.device)),
        takeUntilDestroyed(this.destroyRef),
        take(1)
      )
      .subscribe({
        next: () => {
          this.close();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  close(result?: boolean) {
    this.modalNavigation.result(result);
  }

  cancel() {
    this.close();
  }

  getPoolText() {
    if (this.device?.pool) {
      return this.device.pool.name;
    }
    return 'n. def.';
  }

  setPool(pool: PoolDTO) {
    this.device.pool = pool;
  }

  getChannelText() {
    if (this.device?.channel) {
      return this.device.channel.name;
    }
    return 'n. def.';
  }

  getLocationText() {
    if (this.device?.location) {
      return this.stringTransformation.locationOrCompanyToShortString(this.device.location);
    }
    return 'n. def.';
  }

  selectLocation() {
    const opts: SearchSelectOptions<LocationDTO> = {
      attributeType: 'LOCATION',
      datatype: '',
      title: '',
      singlemode: true,
      initial: this.device.location ? [this.device.location] : null,
    };
    this.modalNavigation.navigate(SbSearchSelectModalComponent, opts);
  }

  onIdentifier($event: string) {
    if ($event && $event.length > 0) {
      this.identifier = $event;
    } else {
      this.identifier = null;
    }

    this.modalNavigation.getSharedData().identifier = this.identifier;
  }

  onName($event: string) {
    if ($event?.length > 0) {
      this.name = $event;
    } else {
      this.name = null;
    }

    this.modalNavigation.getSharedData().name = this.name;
  }

  msUpdate($event: any[], type: string, pScreen?: ScreenDTO) {
    if (!pScreen && (type === 'MEDIUM_TRANSFORMATION' || type === 'SCREEN_HARDWARE')) {
      return;
    }
    const deviceScreen = this.device.screens.find(screen => screen.id === pScreen?.id);

    switch (type) {
      case 'PLAYER_HARDWARE':
        this.device.hardware = null;
        if ($event?.length > 0) {
          this.device.hardware = $event[0];
        }
        break;
      case 'SCREEN_HARDWARE':
        deviceScreen.hardware = null;
        if ($event?.length > 0) {
          deviceScreen.hardware = $event[0];
        }
        break;
      case 'MEDIUM_TRANSFORMATION':
        deviceScreen.mediumTransformation = null;
        if ($event?.length > 0) {
          deviceScreen.mediumTransformation = $event[0];
        }
        break;

      case 'PLAYER_SOFTWARE':
        this.device.software = null;
        if ($event?.length > 0) {
          this.device.software = $event[0];
        }
        break;
    }
  }

  msRequest(type: string, options?: MsOptions) {
    switch (type) {
      case 'PLAYER_HARDWARE':
        if (options) {
          this.msPlayerHardwareOptions = options;
        }
        if (this.msPlayerHardwareOptions?.pagination) {
          const sub = this.api
            .ca_md_PlayerHardware_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msPlayerHardwareOptions?.search),
              },
              this.msPlayerHardwareOptions?.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (p: PlayerHardwareDTO[]) => {
                this.playerHardwareList = p;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
          this.subs.push(sub);
        }
        break;
      case 'SCREEN_HARDWARE':
        if (options) {
          this.msScreenHardwareOptions = options;
        }
        if (this.msScreenHardwareOptions?.pagination) {
          const sub = this.api
            .ca_md_ScreenHardware_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msScreenHardwareOptions?.search),
              },
              this.msScreenHardwareOptions?.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (p: ScreenHardwareDTO[]) => {
                this.screenHardwareList = p;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
          this.subs.push(sub);
        }
        break;
      case 'MEDIUM_TRANSFORMATION':
        if (options) {
          this.msMediumTransformationsOptions = options;
        }
        if (this.msMediumTransformationsOptions?.pagination) {
          const sub = this.api
            .ss_nm_MediumTransformation_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msMediumTransformationsOptions?.search),
              },
              this.msMediumTransformationsOptions?.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (p: MediumTransformationDTO[]) => {
                this.mediumTransformations = p;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
          this.subs.push(sub);
        }
        break;

      case 'PLAYER_SOFTWARE':
        if (options) {
          this.msPlayerSoftwareOptions = options;
        }
        if (this.msPlayerSoftwareOptions?.pagination) {
          const sub = this.api
            .ca_md_PlayerSoftware_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msPlayerSoftwareOptions?.search),
              },
              this.msPlayerSoftwareOptions?.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (p: PlayerSoftwareDTO[]) => {
                this.playerSoftwareList = p;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
          this.subs.push(sub);
        }
        break;
    }
  }

  isCalibrationPossible(): boolean {
    const softwareVersionAttribute: DeviceAttributeDTO = this.device.attributes?.find(
      attribute => attribute.id?.datatype?.id === 'software_version'
    );
    if (!softwareVersionAttribute) {
      return true;
    }
    if (!softwareVersionAttribute.value) {
      return true;
    }
    try {
      const softwareVersion = softwareVersionAttribute.value?.split('_')[1].split('.');
      let major: number = Number(softwareVersion[0]);
      major = !isNaN(major) ? major : 0;
      let minor: number = Number(softwareVersion[1]);
      minor = !isNaN(minor) ? minor : 0;
      let patch: number = Number(softwareVersion[2]);
      patch = !isNaN(patch) ? patch : 0;
      return major > 0 || (major === 0 && minor > 28) || (major === 0 && minor === 28 && patch >= 4);
    } catch (ex) {
      return false;
    }
  }

  updateAttribute(attribute: DeviceAttributeDTO, $event: string[]) {
    attribute.value = $event.join(', ');
  }

  getAttributeInputData(attribute: DeviceAttributeDTO) {
    if (!attribute.value) {
      return [];
    }
    return attribute.value.split(',').map(value => value.trim());
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
    this.subs = [];
  }

  clearDevicePlaylistCache() {
    if (this.isInModalContext) {
      this.modalNavigation.navigate(SbDevicePlaylistCache, { device: this.device });
    }
  }
}
